import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import PasswordStrengthMeter, { scorePassword, MIN_PASSWORD_SCORE } from '../components/PasswordStrengthMeter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { toast } from 'sonner';
import { KeyRound, ShieldCheck, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';
import PasswordMatchIndicator from '../components/PasswordMatchIndicator';
import PasswordRequirements from '../components/PasswordRequirements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('form');
  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');

  const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const handleInit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('New password must be at least 6 characters.');
      return;
    }
    const strength = scorePassword(newPassword, [user?.email || '', user?.name || '']);
    if ((strength?.score ?? 0) < MIN_PASSWORD_SCORE) {
      toast.error('Please choose a stronger password (at least "Fair").');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password/init`, {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }, config());
      toast.success('OTP sent to your email');
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password/verify`, { otp }, config());
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50" data-testid="reset-password-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-2xl mx-auto w-full">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
          data-testid="reset-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <CardTitle>Reset Password</CardTitle>
                <CardDescription>
                  Change your account password. You'll need your current password and a one-time code.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {step === 'form' && (
              <form onSubmit={handleInit} className="space-y-4" data-testid="reset-form">
                <div className="space-y-2">
                  <Label htmlFor="current">Current Password</Label>
                  <PasswordInput
                    id="current"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="h-11"
                    data-testid="reset-current-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new">New Password</Label>
                  <PasswordInput
                    id="new"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="Minimum 6 characters"
                    className="h-11"
                    data-testid="reset-new-input"
                  />
                  <PasswordStrengthMeter password={newPassword} userInputs={[user?.email || '', user?.name || '']} />
                  <PasswordRequirements password={newPassword} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirm New Password</Label>
                  <PasswordInput
                    id="confirm"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-11"
                    data-testid="reset-confirm-input"
                  />
                  <PasswordMatchIndicator password={newPassword} confirm={confirmPassword} />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  data-testid="reset-submit-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {loading ? 'Sending OTP…' : 'Send OTP'}
                </Button>
              </form>
            )}

            {step === 'otp' && (
              <form onSubmit={handleVerify} className="space-y-5" data-testid="reset-otp-form">
                <Alert>
                  <ShieldCheck className="h-4 w-4" />
                  <AlertTitle>OTP sent</AlertTitle>
                  <AlertDescription>
                    A 6-digit code was sent to <b>{user?.email}</b>. It expires in 10 minutes.
                  </AlertDescription>
                </Alert>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="reset-otp-input">
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} className="w-12 h-14 text-xl" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  data-testid="reset-verify-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {loading ? 'Verifying…' : 'Confirm Password Change'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep('form'); setOtp(''); }}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-900"
                  data-testid="reset-back-to-form-button"
                >
                  Back
                </button>
              </form>
            )}

            {step === 'done' && (
              <div className="text-center py-8" data-testid="reset-done-screen">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Password Updated</h3>
                <p className="text-gray-600 mb-6">Your password has been changed successfully.</p>
                <Button
                  onClick={() => navigate(user?.role === 'admin' || user?.role === 'superadmin' ? '/admin' : '/dashboard')}
                  data-testid="reset-done-home-button"
                >
                  Go to Dashboard
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
